import type {CustomFontOptions} from '@stripe-internal/connect-embedded-lib';
import {ConnectEmbeddedLibWrapper} from '@stripe-internal/connect-embedded-lib';
import * as React from 'react';
import {useQueryClient} from 'react-query';
import {useAuthContext} from '../data-layer-client/AuthContext/AuthContext';
import {
  getStripeAdminHost,
  getStripeApiHost,
  getStripeFilesHost,
  getStripeSiteHost,
  getStripeConnectHost,
  getStripeJsHost,
  getStripeDashboardHost,
} from '../data-layer-client/buildStripeClient';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import {useThemeContext} from '../utils/theming/ThemeContext/ThemeContext';
import {useConnectJsOptionsContext} from '../data-layer-client/ConnectJsOptionsContext/useConnectJsOptionsContext';
import {useAttributes} from '../data-layer-client/AttributeContext/useAttributeContext';
import {defaultConnectJsOptions} from './ConnectJsOptions';
import type {FetchOverrideFn} from '../data-layer-client/buildFetchOverride';

type Props = {
  isInternalDomain: boolean;
  customFonts: CustomFontOptions;
  fetchOverride: FetchOverrideFn;
};

export const ConnectEmbeddedLibProvider: React.FC<Props> = ({
  children,
  isInternalDomain,
  customFonts,
  fetchOverride,
}) => {
  const queryClient = useQueryClient();
  const {authInfo} = useAuthContext();
  const theme = useThemeContext();
  const {
    values: {appearance},
  } = useConnectJsOptionsContext();

  // Payment details inline HTML attribute
  const attributes = useAttributes();
  const isInline = attributes.inline?.toLowerCase() === 'true';

  const merchantData = React.useMemo(() => {
    return authInfo && authInfo.merchantData
      ? {
          businessName: authInfo.merchantData.businessName,
          country: authInfo.merchantData.country,
          defaultCurrency: authInfo.merchantData.defaultCurrency,
          merchantId: authInfo.connectedAccountId,
          platformId: authInfo.platformId,
          livemode: authInfo.livemode,
          publishableKey: authInfo.publishableKey,
          applicationName: authInfo.applicationName,
          platformBusinessName: authInfo.platformBusinessName,
          platformNickname: authInfo.platformNickname,
          showSupportLinks:
            authInfo.merchantData.stripeOwnsLossLiability &&
            authInfo.merchantData.stripeOwnsOnboarding &&
            !authInfo.merchantData.hasStandardDashboardAccess,
        }
      : undefined;
  }, [authInfo]);

  return (
    <ConnectEmbeddedLibWrapper
      reactQueryClient={queryClient}
      // @ts-expect-error Type 'FetchOverrideFn' is not assignable to type '(input: RequestInfo | URL, init?:
      fetchFn={fetchOverride}
      merchantData={merchantData}
      serviceEnvironment={{
        apiEndpoint: `https://${getStripeApiHost(
          getCurrentScriptUrlContext().serviceEnvironment,
        )}`,
        fileEndpoint: `https://${getStripeFilesHost(
          getCurrentScriptUrlContext().serviceEnvironment,
        )}/v1/files`,
        siteEndpoint: `https://${getStripeSiteHost(
          getCurrentScriptUrlContext().serviceEnvironment,
        )}`,
        adminEndpoint: `https://${getStripeAdminHost(
          getCurrentScriptUrlContext().serviceEnvironment,
        )}`,
        connectEndpoint: `https://${getStripeConnectHost(
          getCurrentScriptUrlContext().serviceEnvironment,
        )}`,
        stripeJsEndpoint: `https://${getStripeJsHost(
          getCurrentScriptUrlContext().serviceEnvironment,
        )}`,
        dashboardEndpoint: `https://${getStripeDashboardHost(
          getCurrentScriptUrlContext().serviceEnvironment,
        )}`,
      }}
      themingContext={theme.values}
      appearanceWithDefaults={theme.appearanceWithDefaults}
      overlayOption={
        isInline
          ? 'inline'
          : appearance.overlays || defaultConnectJsOptions.appearance.overlays
      }
      isInternalDomain={isInternalDomain}
      customFonts={customFonts}
    >
      {children}
    </ConnectEmbeddedLibWrapper>
  );
};
